import React, { useCallback, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  ENTITY_TYPE_OPTIONS,
  ENTITY_TYPE_FROM_COMPANY_TYPE,
  COMPANY_TYPE_FROM_ENTITY_TYPE,
  FORMATION_PROOF_BY_ENTITY_TYPE,
  GST_STATUS,
  GST_STATUS_OPTIONS
} from '../../constants/kyb';
import {
  isValidGstinChecksum,
  panFromGstin,
  collectIdentifierWarnings
} from '../../utils/identifierChecks';
import SmartFillPanel from './SmartFillPanel';

/** Draft canonical name → form field. Mirrors draftToCompanyPayload on the backend. */
const DRAFT_FIELD_TO_FORM = {
  legal_entity_name: 'company_name',
  trade_name: 'trade_name',
  entity_type: 'entity_type',
  pan: 'pan',
  gstin: 'gst',
  registered_address: 'address'
};

import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import { state_arr } from '../../static/data/statesAndCities';
import { getStateAndCity } from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { capitalizeWords } from '../../utils/stringUtils';

const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const gstRegex = /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}Z[0-9A-Za-z]{1}$/;
const noSpaceRegex = /^\S*$/; // No spaces allowed
const companyNameRegex = /^[A-Za-z0-9\s!@#$%^&*()_+\-=[\]{};:'",.<>?]+$/;
const pincodeRegex = /^[0-9]{6}$/; // Only 6 digits allowed

const validationSchema = yup.object().shape({
  company_name: yup
    .string()
    .required('Company name is required')
    .matches(companyNameRegex, 'Company name should only contain letters and numbers')
    .max(50, 'Company name should be at most 50 characters'),
  company_type: yup.string().required('Company type is required'),
  address: yup
    .string()
    .required('Street Address is required')
    .min(10, 'Street Address should be at least 10 characters')
    .max(200, 'Street Address should be at most 200 characters'),
  zip_code: yup
    .string()
    .required('Zip/Pin code is required')
    .matches(pincodeRegex, 'Zip/Pin code should be exactly 6 digits'),
  country: yup.string().required('Country is required').oneOf(['India'], 'Country must be India'),
  state: yup.string().required('State is required'),
  city: yup.string().required('City is required'),
  pan: yup
    .string()
    .required('PAN is required')
    .matches(panRegex, 'Invalid PAN format')
    .matches(noSpaceRegex, 'PAN should not contain spaces'),
  entity_type: yup.string().required('Legal entity type is required'),
  trade_name: yup.string().max(255, 'Trade name should be at most 255 characters'),
  gst_status: yup
    .string()
    .required('Declare whether the business is GST registered')
    .oneOf([GST_STATUS.REGISTERED, GST_STATUS.NOT_REGISTERED]),
  // GST is required only when the business is declared GST registered. The backend
  // enforces the same rule; this is instant feedback, not the authority.
  gst: yup.string().when('gst_status', {
    is: GST_STATUS.REGISTERED,
    then: (schema) =>
      schema
        .required('GST is required')
        .matches(gstRegex, 'Invalid GST format')
        .matches(noSpaceRegex, 'GST should not contain spaces')
        .test(
          'gstin-checksum',
          'GSTIN check digit is wrong — the number looks mistyped.',
          (value) => !value || isValidGstinChecksum(value)
        )
        .test(
          'gstin-contains-pan',
          'This GSTIN belongs to a different PAN. Check both documents are for the same company.',
          function (value) {
            if (!value || !isValidGstinChecksum(value)) return true;
            const { pan } = this.parent;
            if (!pan || !panRegex.test(pan)) return true;
            return panFromGstin(value) === pan.toUpperCase();
          }
        ),
    otherwise: (schema) => schema.notRequired()
  })
});

const AddCompany = ({ onSubmit, onClose, isSubmitting }) => {
  const userId = Cookies.get('userId');
  const [city, setCity] = useState('Select');
  const [state, setState] = useState('-');
  const [cityDropdownOptions, setCityDropdownOptions] = useState([]);
  const [tehsilDropdownOptions, setTehsilDropdownOptions] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { gst_status: GST_STATUS.REGISTERED }
  });

  const entityType = watch('entity_type');
  const gstStatus = watch('gst_status');
  const watchedGst = watch('gst');
  const watchedPan = watch('pan');
  const watchedState = watch('state');

  /**
   * Advisory only — a cross-state GST registration is legal, so these are shown as
   * notices and never block submission. Blocking problems come through `errors`.
   */
  const identifierWarnings = useMemo(
    () =>
      collectIdentifierWarnings({
        gstin: gstStatus === GST_STATUS.REGISTERED ? watchedGst : null,
        state: watchedState || state,
        pan: watchedPan,
        entityType
      }),
    [gstStatus, watchedGst, watchedPan, watchedState, state, entityType]
  );

  const requiredProofs = entityType ? FORMATION_PROOF_BY_ENTITY_TYPE[entityType] : null;

  /**
   * Fields populated by Smart Fill, keyed by form field name. Each entry keeps the
   * model's confidence and evidence quote so the user can check the value against the
   * source. Editing a field removes it from here — a confirmed value is the user's, not
   * the model's, and must not keep advertising a confidence score.
   */
  const [aiFields, setAiFields] = useState({});

  const applyDraft = useCallback(
    (draft) => {
      const applied = {};
      Object.entries(draft?.fields || {}).forEach(([canonicalName, field]) => {
        const formField = DRAFT_FIELD_TO_FORM[canonicalName];
        if (!formField || !field?.value) return;
        setValue(formField, field.value, { shouldValidate: true });

        // Legal entity type also determines the legacy company type — fill it rather
        // than making the operator answer the same question twice.
        if (canonicalName === 'entity_type') {
          const legacyType = COMPANY_TYPE_FROM_ENTITY_TYPE[field.value];
          if (legacyType) setValue('company_type', legacyType, { shouldValidate: true });
        }

        applied[formField] = {
          confidence: field.confidence,
          evidence: field.evidence,
          source: field.source,
          conflict: field.conflict
        };
      });
      setAiFields((prev) => ({ ...prev, ...applied }));
    },
    [setValue]
  );

  /** Any edit to an AI-filled field promotes it to a user-owned value. */
  const clearAiMark = useCallback((formField) => {
    setAiFields((prev) => {
      if (!prev[formField]) return prev;
      const next = { ...prev };
      delete next[formField];
      return next;
    });
  }, []);

  const aiCount = Object.keys(aiFields).length;

  /** Pre-fill the legal entity type from the existing company type where unambiguous. */
  const handleCompanyTypeChange = useCallback(
    (value) => {
      setValue('company_type', value);
      const suggested = ENTITY_TYPE_FROM_COMPANY_TYPE[value];
      if (suggested) setValue('entity_type', suggested, { shouldValidate: true });
    },
    [setValue]
  );

  const handlePincodeChange = useCallback(
    async (pincode) => {
      const cleanedPincode = pincode.replace(/[^0-9]/g, '');
      setValue('zip_code', cleanedPincode);

      if (cleanedPincode.length === 6) {
        try {
          const pincodeList = await getStateAndCity(cleanedPincode);
          console.log(pincodeList);
          const postOfficeList = pincodeList[0].PostOffice;

          if (postOfficeList.length > 0) {
            const newState = postOfficeList[0].State;
            const uniqueCircles = [
              ...new Set(
                postOfficeList
                  ?.map((office) => {
                    const circle =
                      office.Name && office.Name !== 'NA' && office.Name.trim() !== ''
                        ? office.Name
                        : office.Block;
                    return circle && circle !== 'NA' && circle.trim() !== '' ? circle : null;
                  })
                  .filter(Boolean)
              )
            ];
            const uniqueTehsils = [...new Set(postOfficeList?.map((office) => office.Block))];

            console.log(uniqueCircles);
            const newCityOptions = uniqueCircles?.map((circle) => ({
              value: circle,
              label: circle
            }));
            setCityDropdownOptions(
              uniqueTehsils?.map((tehsil) => ({ value: tehsil, label: tehsil }))
            );
            const newCity = uniqueCircles[0];

            setValue('state', newState);
            // setCityDropdownOptions(newCityOptions);
            setValue('city', uniqueTehsils[0]);

            setState(newState);
            setCity(newCity);
          } else {
            setValue('state', '-');
            setValue('city', 'Select');
            setCityDropdownOptions([]);

            setState('-');
            setCity('Select');
          }
        } catch (error) {
          toast.error('The entered pincode is invalid. Please enter a valid pincode.');
          setValue('state', '-');
          setValue('city', 'Select');
          setCityDropdownOptions([]);

          setState('-');
          setCity('Select');
        }
      }
    },
    [setValue]
  );

  const handlePanChange = useCallback(
    async (pan) => {
      const cleanedPan = pan.toUpperCase().replace(/[^a-zA-Z0-9]/g, '');
      setValue('pan', cleanedPan);
      clearAiMark('pan');
    },
    [setValue, clearAiMark]
  );

  const handleGSTChange = useCallback(
    async (gst) => {
      const cleanedGST = gst.toUpperCase().replace(/[^a-zA-Z0-9]/g, '');
      setValue('gst', cleanedGST);
      clearAiMark('gst');
    },
    [setValue, clearAiMark]
  );

  const handleCompanyName = useCallback(
    async (val) => {
      const trimmedValue = val.trimStart(); // Removes leading spaces
      const sanitizedValue = trimmedValue.replace(
        /[^a-zA-Z0-9\s!@#$%^&*()_+\-=[\]{};:'",.<>?]/g,
        ''
      ); // Allow alphanumeric and special characters
      setValue('company_name', sanitizedValue);
      clearAiMark('company_name');
    },
    [setValue, clearAiMark]
  );

  const handleAddressChange = useCallback(
    async (val) => {
      const trimmedValue = val.trimStart(); // Removes leading spaces
      setValue('address', trimmedValue);
      clearAiMark('address');
    },
    [setValue, clearAiMark]
  );

  const handleSubmitForm = (data) => {
    const trimmedData = {
      ...data,
      company_name: data.company_name.trim(), // Trim leading and trailing spaces on submit
      address: data.address.trim(), // Trim leading and trailing spaces on submit
      trade_name: data.trade_name?.trim() || undefined
    };

    // Declaring "not registered" must send no GSTIN at all — an empty string would fail
    // the backend's format check instead of being read as a declaration.
    if (trimmedData.gst_status === GST_STATUS.NOT_REGISTERED) {
      delete trimmedData.gst;
    }

    onSubmit(trimmedData);
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleSubmitForm)}>
      <SmartFillPanel onApplyDraft={applyDraft} disabled={isSubmitting} />

      <div className="flex flex-col justify-between items-start gap-4">
        <div className="flex w-full items-center justify-between border-b-[1px] border-stone-200 pb-2">
          <span className="text-lg">Company Details</span>
          {aiCount > 0 && (
            <span className="text-xs text-stone-500">
              ✨ {aiCount} field{aiCount === 1 ? '' : 's'} suggested — check each against your
              document
            </span>
          )}
        </div>

        {aiCount > 0 && (
          <div className="w-full rounded border border-stone-200 bg-stone-50 px-3 py-2">
            <p className="text-xs text-stone-600">
              Suggested values are editable. Nothing is saved until you press Add.
            </p>
            <ul className="mt-1 flex flex-col gap-0.5">
              {Object.entries(aiFields).map(([formField, meta]) => (
                <li key={formField} className="text-xs text-stone-500">
                  ✨ <span className="text-stone-700">{formField.replace(/_/g, ' ')}</span>
                  {typeof meta.confidence === 'number' &&
                    ` · ${Math.round(meta.confidence * 100)}% confidence`}
                  {meta.source && ` · from ${String(meta.source).replace(/_/g, ' ')}`}
                  {meta.evidence && (
                    <span className="text-stone-400" title={meta.evidence}>
                      {' '}
                      · “{meta.evidence.slice(0, 60)}
                      {meta.evidence.length > 60 ? '…' : ''}”
                    </span>
                  )}
                  {meta.conflict && (
                    <span className="text-amber-700">
                      {' '}
                      · conflicts with “{meta.conflict.value}” from{' '}
                      {String(meta.conflict.source).replace(/_/g, ' ')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="w-full flex flex-col gap-2">
          <div className="flex justify-between space-x-4">
            <FormInput
              width={46}
              register={register}
              isRequired
              register_key={'company_name'}
              label={'Company Name'}
              type={'text'}
              maxLength={50}
              errors={errors.company_name}
              onType={handleCompanyName}
            />

            <FormInput
              width={46}
              register={register}
              isRequired
              register_key={'company_type'}
              label={'Company Type'}
              type={'dropdown'}
              options={[
                { value: '', label: 'Select', disabled: true, selected: true },
                { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
                { value: 'Partnership', label: 'Partnership' },
                { value: 'Limited Liability (LLC)', label: 'Limited Liability (LLC)' },
                { value: 'Public Limited (Ltd)', label: 'Public Limited (Ltd)' },
                { value: 'Private Limited (Pvt Ltd)', label: 'Private Limited (Pvt Ltd)' },
                { value: 'Nonprofit Organization', label: 'Nonprofit Organization' },
                { value: 'Co-operative', label: 'Cooperative' },
                { value: 'Joint Venture', label: 'Joint Venture' }
              ]}
              errors={errors.company_type}
              onType={handleCompanyTypeChange}
            />
          </div>
        </div>

        <div className="w-full flex flex-col gap-2">
          <div className="flex justify-between space-x-4">
            <FormInput
              width={46}
              register={register}
              isRequired
              register_key={'entity_type'}
              label={'Legal Entity Type'}
              type={'dropdown'}
              options={ENTITY_TYPE_OPTIONS}
              errors={errors.entity_type}
            />

            <FormInput
              width={46}
              register={register}
              register_key={'trade_name'}
              label={'Trade Name (if different)'}
              type={'text'}
              maxLength={255}
              errors={errors.trade_name}
              placeholder={'Leave blank if same as legal name'}
            />
          </div>
          {requiredProofs && (
            <p className="text-xs text-stone-500">
              Formation proof required for {entityType}: {requiredProofs.join(', or ')}.
            </p>
          )}
        </div>

        <div className="w-full flex justify-between items-center">
          <FormInput
            width={46}
            register={register}
            isRequired
            register_key={'pan'}
            label={'Company PAN'}
            type={'text'}
            maxLength={10}
            // CustomStyle={{ textTransform: 'uppercase' }}
            errors={errors.pan}
            onType={handlePanChange}
            placeholder={'ABCDE1234F'}
          />

          <FormInput
            width={46}
            register={register}
            isRequired
            register_key={'gst_status'}
            label={'GST Status'}
            type={'dropdown'}
            options={GST_STATUS_OPTIONS}
            errors={errors.gst_status}
          />
        </div>

        {gstStatus === GST_STATUS.REGISTERED ? (
          <div className="w-full flex justify-between items-center">
            <FormInput
              width={46}
              register={register}
              isRequired
              register_key={'gst'}
              label={'Company GST'}
              type={'text'}
              errors={errors.gst}
              onType={handleGSTChange}
              maxLength={15}
              placeholder={'27ABCDE1234F1Z5'}
            />
          </div>
        ) : (
          <p className="text-xs text-stone-500">
            Recorded as a declaration that this business is not GST registered. A formation
            proof is still required.
          </p>
        )}

        {identifierWarnings.length > 0 && (
          <div className="w-full rounded border border-amber-300 bg-amber-50 px-3 py-2">
            {identifierWarnings.map((warning) => (
              <p key={warning} className="text-xs text-amber-800">
                {warning}
              </p>
            ))}
          </div>
        )}

        <div className="w-full flex flex-col gap-2">
          <FormInput
            width={100}
            register={register}
            isRequired
            register_key={'address'}
            label={'Street Address'}
            type={'text'}
            maxLength={200}
            errors={errors.address}
            onType={handleAddressChange}
            placeholder={'Enter complete street address'}
          />
        </div>

        <div className="w-full flex flex-col gap-2">
          <div className="flex justify-between space-x-4">
            <FormInput
              width={46}
              register={register}
              isRequired
              register_key={'zip_code'}
              label={'Zip/Pin Code'}
              type={'text'}
              maxLength={6}
              errors={errors.zip_code}
              onType={handlePincodeChange}
            />

            <FormInput
              width={46}
              register={register}
              isRequired
              register_key={'country'}
              label={'Select Country'}
              valuePre={'India'}
              errors={errors.country}
              CustomStyle={{ pointerEvents: 'none' }}
            />
          </div>
        </div>

        <div className="w-full flex flex-col gap-2">
          <div className="flex justify-between space-x-4">
            <FormInput
              width={46}
              register={register}
              isRequired
              register_key={'state'}
              label={'State'}
              errors={errors.state}
              valuePre={state}
              CustomStyle={{ pointerEvents: 'none' }}
            />
            <FormInput
              width={46}
              register={register}
              isRequired
              register_key={'city'}
              label={'City'}
              defaultValuePre={city}
              type={'dropdown'}
              options={cityDropdownOptions}
              errors={errors.city}
            />
          </div>
        </div>
      </div>

      <div className="w-full flex justify-end items-center space-x-4">
        <Button type="reset" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
          Add
        </Button>
      </div>
    </form>
  );
};

export default AddCompany;
